<template>
    <div class="topbar-shell">
        <div class="space-y-4">
            <div class="flex items-start gap-3 sm:gap-4">
                <div
                    class="w-10 h-10 sm:w-12 sm:h-12 rounded-full topbar-check-wrap flex items-center justify-center flex-shrink-0">
                    <svg xmlns="http://www.w3.org/2000/svg" class="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24"
                        fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round">
                        <path d="M20 6L9 17l-5-5" />
                    </svg>
                </div>

                <div class="min-w-0">
                    <div class="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                        <h3 class="text-base sm:text-lg leading-tight font-semibold text-theme-secondary">
                            Order confirmed
                        </h3>
                        <span class="text-theme-body-light hidden sm:inline">&middot;</span>
                        <span class="text-base sm:text-lg leading-tight font-semibold text-theme-secondary">#{{
                            orderNumber || "-"
                        }}</span>
                        <span
                            class="inline-flex items-center rounded-full px-2 py-0.5 text-xxs sm:text-xs font-semibold uppercase tracking-wide"
                            :class="isOnHold ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'">
                            {{ statusLabel }}
                        </span>
                    </div>
                    <p class="text-2xl leading-tight font-semibold text-theme-secondary" v-if="false">#{{ orderNumber ||
                        "-" }}</p>
                    <p class="text-xs sm:text-sm text-theme-body mt-1.5">{{ subtitle }}</p>
                    <p
                        class="text-xs sm:text-sm mt-1 text-theme-secondary inline-flex items-center gap-1.5 font-medium">
                        <span aria-hidden="true">❤️</span>
                        <span>Thank you for choosing Medusa.</span>
                    </p>
                </div>
            </div>

            <div class="flex items-center gap-2 flex-wrap">
                <router-link v-if="invoices.length" target="_blank" :to="'/invoice/' + invoices[0].id"
                    class="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-md border border-theme-border border-opacity-60 text-theme-secondary text-xxs sm:text-xs font-semibold bg-white hover:bg-theme-bg-shade hover:bg-opacity-30 transition-colors">
                    <Icon name="document-file" class="w-3.5 h-3.5" />
                    <span>Invoice</span>
                </router-link>
                <InvoiceCsvExportPreview v-if="orderId"
                    :order-id="orderId"
                    :order-number="orderNumber"
                    :invoice-number="invoices[0]?.invoice_number || ''"
                    :company-name="billingName"
                    button-label="Export"
                    button-class="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-md border border-theme-border border-opacity-60 text-theme-secondary text-xxs sm:text-xs font-semibold bg-white hover:bg-theme-bg-shade hover:bg-opacity-30 transition-colors" />
                <router-link :to="viewOrderTo"
                    class="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-md border border-theme-border border-opacity-60 text-theme-secondary text-xxs sm:text-xs font-semibold bg-white hover:bg-theme-bg-shade hover:bg-opacity-30 transition-colors"
                    v-if="viewOrderTo">
                    <span>View order</span>
                </router-link>
                <router-link to="/home"
                    class="inline-flex items-center justify-center gap-1.5 px-3 sm:px-4 py-2 rounded-md border border-theme-secondary text-white text-xxs sm:text-xs font-semibold bg-theme-secondary hover:bg-opacity-95 transition-colors">
                    <Icon name="right-arrow" class="w-3.5 h-3.5" />
                    <span>Continue shopping</span>
                </router-link>
            </div>

            <div class="h-px bg-theme-border bg-opacity-60"></div>

            <div class="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                    <p class="text-xxs uppercase tracking-wider text-theme-body-light font-medium">Shipping To</p>
                    <p class="text-theme-secondary text-xs sm:text-sm font-semibold mt-1">{{ shippingName || "-" }}
                    </p>
                    <p class="text-theme-body text-xxs sm:text-xs mt-0.5 whitespace-pre-line leading-relaxed">{{
                        shippingAddress }}</p>
                    <a v-if="shippingHelpText" href="#" @click.prevent="openChat"
                        class="inline-block mt-1 text-theme-body underline text-xxs">{{
                        shippingHelpText }}</a>
                </div>
                <div>
                    <p class="text-xxs uppercase tracking-wider text-theme-body-light font-medium">Billing</p>
                    <p class="text-theme-secondary text-xs sm:text-sm font-semibold mt-1">{{ billingName || "-" }}
                    </p>
                    <p class="text-theme-body text-xxs sm:text-xs mt-0.5 whitespace-pre-line leading-relaxed">{{
                        billingAddress }}</p>
                </div>
                <div>
                    <p class="text-xxs uppercase tracking-wider text-theme-body-light font-medium">Your Rep</p>
                    <div class="topbar-rep mt-1">
                        <span class="topbar-rep-avatar">{{ repInitials }}</span>
                        <div class="min-w-0 flex-1">
                            <p class="text-theme-secondary text-xs sm:text-sm font-semibold leading-tight">{{ repName ||
                                "-" }}</p>
                            <a v-if="repPhone" :href="`tel:${repPhone}`" class="topbar-rep-link">{{ repPhone }}</a>
                            <a v-if="repEmail" :href="`mailto:${repEmail}`" class="topbar-rep-link">{{ repEmail }}</a>
                            <a v-if="repEmail" href="#" @click="onRepContact"
                                class="topbar-rep-link topbar-rep-link-action">Contact rep</a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { computed } from "vue";
import InvoiceCsvExportPreview from "@/components/InvoiceCsvExportPreview.vue";

const props = defineProps({
    isOnHold: { type: Boolean, default: false },
    statusLabel: { type: String, default: "" },
    subtitle: { type: String, default: "" },
    invoices: { type: Array, default: () => [] },
    viewOrderTo: { type: String, default: "" },
    orderId: { type: [String, Number], default: "" },
    orderNumber: { type: [String, Number], default: "" },
    referenceNumber: { type: [String, Number], default: "" },
    paymentLabel: { type: String, default: "" },
    shippingName: { type: String, default: "" },
    shippingAddress: { type: String, default: "" },
    billingName: { type: String, default: "" },
    billingAddress: { type: String, default: "" },
    repName: { type: String, default: "" },
    repEmail: { type: String, default: "" },
    repPhone: { type: String, default: "" },
    shippingHelpText: { type: String, default: "" },
    placedAt: { type: String, default: "" },
});

const onRepContact = (e) => {
    e.preventDefault();
    document.querySelector("#rep-panel button")?.click();
};

const openChat = () => {
    window.Intercom && window.Intercom("show");
};

const repInitials = computed(() => {
    const source = String(props.repName || "").trim();
    if (!source) return "MR";
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] || ""}${parts[parts.length - 1][0] || ""}`.toUpperCase();
});
</script>

<style scoped>
.topbar-shell {
    border: 1px solid rgba(230, 232, 238, 0.7);
    border-radius: 14px;
    background: #fff;
    box-shadow: none !important;
    padding: 22px 26px;
}

.topbar-check-wrap {
    background: #e7f3e9;
    color: #2f7a3e;
}

.topbar-rep {
    display: flex;
    align-items: flex-start;
    gap: 10px;
}

.topbar-rep-avatar {
    width: 36px;
    height: 36px;
    border-radius: 999px;
    background: #ecdebf;
    color: #7a5b14;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 700;
    flex-shrink: 0;
}

.topbar-rep-link {
    display: block;
    margin-top: 2px;
    font-size: 12px;
    color: #6d7688;
    line-height: 1.3;
    text-decoration: none;
}

.topbar-rep-link-action {
    margin-top: 4px;
    text-decoration: underline;
    text-underline-offset: 2px;
    color: #2b303f;
    font-weight: 600;
}
</style>
